import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import StatCard from "../../components/admin/StatCard";
import toast from "react-hot-toast";
import { getJobsAdminData, moderateJobEntity } from "../../api/axios";

const JOB_STATUS_STYLES = {
  open: "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  hidden: "bg-amber-100 text-amber-700 border-amber-200",
};

export default function JobsAdmin() {
  const [data, setData] = useState({ jobs: [], availability: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("jobs");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await getJobsAdminData();
      setData(res.data || { jobs: [], availability: [], stats: {} });
    } catch (err) {
      toast.error(err.message || "Failed to load jobs data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (entity, id, newStatus) => {
    try {
      await moderateJobEntity(entity, id, newStatus);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(e.message || "Failed");
    }
  };

  const filteredJobs = useMemo(() => {
    return (data.jobs || []).filter((j) => {
      const q = search.trim().toLowerCase();
      const matchesSearch = !q || [j.post_name, j.hospital_name, j.job_location, j.author_name].some((v) => String(v || "").toLowerCase().includes(q));
      const matchesStatus = !status || j.status === status;
      return matchesSearch && matchesStatus;
    });
  }, [data.jobs, search, status]);

  const filteredAvailability = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.availability || [];
    return (data.availability || []).filter((a) => [a.doctor_name, a.specialty, a.location].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [data.availability, search]);

  const stats = data.stats || {};

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#00342b]">Jobs Management</h2>
          <p className="text-sm text-[#3f4945] mt-1">Manage job postings and doctor availability listings</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <StatCard title="Total Jobs" value={stats.total_jobs ?? 0} subtitle="All job posts" />
          <StatCard title="Open" value={stats.open ?? 0} subtitle="Accepting applicants" />
          <StatCard title="Closed" value={stats.closed ?? 0} subtitle="No longer open" />
          <StatCard title="Hidden" value={stats.hidden ?? 0} subtitle="Hidden by moderators" />
          <StatCard title="Available Doctors" value={stats.available_doctors ?? 0} subtitle="Open to opportunities" />
        </div>

        <div className="flex gap-3 flex-wrap">
          <button onClick={() => setTab("jobs")} className={`px-4 py-2 rounded-full border text-sm font-semibold transition ${tab === "jobs" ? "bg-[#00342b] text-white border-[#00342b]" : "bg-white text-[#3f4945]"}`}>Job Posts ({data.jobs?.length || 0})</button>
          <button onClick={() => setTab("availability")} className={`px-4 py-2 rounded-full border text-sm font-semibold transition ${tab === "availability" ? "bg-[#00342b] text-white border-[#00342b]" : "bg-white text-[#3f4945]"}`}>Available Doctors ({data.availability?.length || 0})</button>
        </div>

        <div className="bg-white rounded-2xl border border-[#e1e3e4] p-4 grid gap-3 md:grid-cols-4">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === "jobs" ? "Search title / hospital / location..." : "Search doctor / specialty / location..."}
            className="border border-[#bfc9c4] rounded-xl px-4 py-3 md:col-span-3 text-sm focus:ring-2 focus:ring-[#00342b] outline-none"
          />
          {tab === "jobs" && (
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-[#bfc9c4] rounded-xl px-4 py-3 bg-white text-sm">
              <option value="">All Status</option>
              <option value="open">✅ Open</option>
              <option value="closed">⏹ Closed</option>
              <option value="hidden">⛔ Hidden</option>
            </select>
          )}
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border border-[#e1e3e4] p-10 text-center text-[#3f4945]">Loading…</div>
        ) : tab === "jobs" ? (
          <div className="bg-white rounded-2xl border border-[#e1e3e4] divide-y divide-[#f0f1f1]">
            {filteredJobs.map((j) => (
              <div key={j.id} className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase border ${JOB_STATUS_STYLES[j.status] || JOB_STATUS_STYLES.hidden}`}>{j.status}</span>
                    <span className="text-xs text-[#707975]">{j.vacancy_available} {Number(j.vacancy_available) === 1 ? "opening" : "openings"}</span>
                  </div>
                  <div className="font-bold text-[#191c1d] mt-1.5">{j.post_name}</div>
                  <div className="text-sm text-[#707975] mt-0.5">{j.hospital_name} • {j.job_location} • Posted by {j.author_name || "Unknown"}</div>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  {j.status !== "open" && (
                    <button onClick={() => act("job", j.id, "open")} className="px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition">Open</button>
                  )}
                  {j.status !== "closed" && (
                    <button onClick={() => act("job", j.id, "closed")} className="px-3 py-2 rounded-lg border border-[#bfc9c4] text-xs font-semibold text-[#3f4945] hover:bg-gray-50 transition">Close</button>
                  )}
                  {j.status !== "hidden" && (
                    <button onClick={() => act("job", j.id, "hidden")} className="px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition">Hide</button>
                  )}
                </div>
              </div>
            ))}
            {!filteredJobs.length && <p className="text-[#a0aba5] text-center p-10">No job posts found.</p>}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#e1e3e4] divide-y divide-[#f0f1f1]">
            {filteredAvailability.map((a) => (
              <div key={a.id} className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase border ${a.status === "open" ? JOB_STATUS_STYLES.open : JOB_STATUS_STYLES.hidden}`}>{a.status}</span>
                  </div>
                  <div className="font-bold text-[#191c1d] mt-1.5">{a.doctor_name || "Unknown"}</div>
                  <div className="text-sm text-[#707975] mt-0.5">{[a.specialty || a.doctor_specialty, a.subspecialty, a.location].filter(Boolean).join(" • ")}</div>
                  <div className="text-xs text-[#707975] mt-0.5">{a.email} • {a.phone}</div>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  {a.status !== "open" && (
                    <button onClick={() => act("availability", a.id, "open")} className="px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition">Show</button>
                  )}
                  {a.status !== "hidden" && (
                    <button onClick={() => act("availability", a.id, "hidden")} className="px-3 py-2 rounded-lg border border-[#bfc9c4] text-xs font-semibold text-[#3f4945] hover:bg-gray-50 transition">Hide</button>
                  )}
                </div>
              </div>
            ))}
            {!filteredAvailability.length && <p className="text-[#a0aba5] text-center p-10">No availability listings found.</p>}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
